/**
 * Cookie-consent contract shared by blueticks.co (this app) and app.blueticks.co.
 *
 * Framework-free and DOM-free on purpose: the web app copies this file verbatim so both
 * surfaces read and write the same `bt_consent` cookie with the same schema and versions.
 * Any change here is a contract change: bump CONSENT_SCHEMA_VERSION or CONSENT_BANNER_VERSION
 * and update the copy in whatsapp-scheduler.
 *
 * Spec: legal/serenix-compliance/23-cookie-consent-spec.md (sections 5, 6, 7).
 */

export const CONSENT_COOKIE_NAME = 'bt_consent';
/** Shape of the stored JSON. Readers reject anything else. */
export const CONSENT_SCHEMA_VERSION = 1;
/** Bump when the privacy policy's cookie/tracker description changes. Recorded, not enforced. */
export const CONSENT_POLICY_VERSION = '2026-10-21';
/** Bump when the banner's categories or vendor list change. A mismatch forces re-prompt. */
export const CONSENT_BANNER_VERSION = '1';
/** Six months, per spec section 7. */
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 30 * 6;
/** Production API origin; the consent log writes here only from production hosts. */
export const PROD_API_BASE = 'https://api.blueticks.co';
/** Fire-and-forget consent evidence endpoint (spec section 6), on the API origin of the running environment (#1031). */
export const consentLogUrl = (apiBase: string): string => `${apiBase.replace(/\/+$/, '')}/consent-log`;
export const GOOGLE_ANALYTICS_ID = 'G-RCEGPEQEZ5';
export const CRISP_WEBSITE_ID = 'f317863d-feb0-4f63-9ae2-fb8280d54a53';

/** `functional` is fixed to true: Crisp only loads on the visitor's own click or after consent. */
export interface ConsentChoices {
  analytics: boolean;
  marketing: boolean;
  functional: true;
}

export type ConsentSource =
  | 'banner_accept_all'
  | 'banner_reject_all'
  | 'banner_custom'
  | 'settings_withdraw'
  /** Opt-out region: trackers loaded by default, no visitor choice yet (logged once per session). */
  | 'default_grant'
  /** Opt-out region with Global Privacy Control: marketing denied by default, analytics kept. */
  | 'gpc_opt_out';

/**
 * opt_in: deny by default + banner (prior consent required). opt_out: trackers load, no banner,
 * Cookie settings link allows opting out. unknown_denied: location missing or failed; treated as opt_in.
 */
export type Jurisdiction = 'opt_in' | 'opt_out' | 'unknown_denied';

export interface StoredConsent {
  /** Cookie schema version. */
  v: number;
  consentId: string;
  choices: ConsentChoices;
  policyVersion: string;
  /** String, matching the consent-log endpoint's version field. */
  bannerVersion: string;
  /** ISO timestamp of the decision. */
  ts: string;
}

export const ALL_DENIED: ConsentChoices = { analytics: false, marketing: false, functional: true };
export const ALL_GRANTED: ConsentChoices = { analytics: true, marketing: true, functional: true };

export function makeChoices(analytics: boolean, marketing: boolean): ConsentChoices {
  return { analytics, marketing, functional: true };
}

/** Google Consent Mode v2 defaults: everything non-essential denied (spec section 4). */
export const CONSENT_MODE_DEFAULTS = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
  wait_for_update: 500,
} as const;

/** Payload for `gtag('consent', 'update', ...)` for the given choices. */
export function consentModeUpdate(choices: ConsentChoices): Record<string, string> {
  const granted = (on: boolean) => (on ? 'granted' : 'denied');
  return {
    analytics_storage: granted(choices.analytics),
    ad_storage: granted(choices.marketing),
    ad_user_data: granted(choices.marketing),
    ad_personalization: granted(choices.marketing),
  };
}

export function buildStoredConsent(input: {
  consentId: string;
  choices: ConsentChoices;
  now: Date;
}): StoredConsent {
  return {
    v: CONSENT_SCHEMA_VERSION,
    consentId: input.consentId,
    choices: { analytics: !!input.choices.analytics, marketing: !!input.choices.marketing, functional: true },
    policyVersion: CONSENT_POLICY_VERSION,
    bannerVersion: CONSENT_BANNER_VERSION,
    ts: input.now.toISOString(),
  };
}

/** Returns the parsed record, or null for anything malformed or from another schema version. */
export function parseStoredConsent(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const c = data as Record<string, unknown>;
  const choices = c.choices as Record<string, unknown> | undefined;
  if (
    c.v !== CONSENT_SCHEMA_VERSION ||
    typeof c.consentId !== 'string' ||
    c.consentId.length === 0 ||
    typeof c.policyVersion !== 'string' ||
    typeof c.bannerVersion !== 'string' ||
    typeof c.ts !== 'string' ||
    !choices ||
    typeof choices.analytics !== 'boolean' ||
    typeof choices.marketing !== 'boolean'
  ) {
    return null;
  }
  return {
    v: c.v,
    consentId: c.consentId,
    choices: { analytics: choices.analytics, marketing: choices.marketing, functional: true },
    policyVersion: c.policyVersion,
    bannerVersion: c.bannerVersion,
    ts: c.ts,
  };
}

/** A stored choice counts only while the banner version matches. Otherwise the visitor is re-prompted. */
export function isCurrentConsent(c: StoredConsent | null): c is StoredConsent {
  return !!c && c.bannerVersion === CONSENT_BANNER_VERSION;
}

export function serializeStoredConsent(c: StoredConsent): string {
  return JSON.stringify(c);
}

/** Cookie value is percent-encoded JSON. */
export function encodeConsentCookieValue(c: StoredConsent): string {
  return encodeURIComponent(serializeStoredConsent(c));
}

export function decodeConsentCookieValue(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/** Cookie domain shared by blueticks.co and app.blueticks.co; localhost and other hosts stay host-only. */
export function consentCookieDomain(hostname: string): string | undefined {
  return hostname === 'blueticks.co' || hostname.endsWith('.blueticks.co') ? '.blueticks.co' : undefined;
}

/** Full Set-Cookie-style string for bt_consent. */
export function buildConsentCookie(c: StoredConsent, hostname: string): string {
  const domain = consentCookieDomain(hostname);
  return [
    `${CONSENT_COOKIE_NAME}=${encodeConsentCookieValue(c)}`,
    'Path=/',
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    'Secure',
    ...(domain ? [`Domain=${domain}`] : []),
  ].join('; ');
}

/**
 * Cookies that identify a visitor for analytics, advertising or affiliate tracking.
 * Withdrawal removes them. bt_consent itself is never in this list.
 */
export const TRACKER_COOKIE_PATTERNS: readonly RegExp[] = [
  /^_ga(_|$)/, // GA4 client and session cookies (_ga, _ga_<ID>)
  /^_gid$/,
  /^_gcl_/, // Google Ads conversion linker (_gcl_au, _gcl_aw, _gcl_dc, ...)
  /^GCL_AW_P$/,
  /^_fprom_(tid|ref)$/, // FirstPromoter
  /^bt_referral$/, // Blueticks first-touch affiliate cookie
  /^blueticks_attribution$/,
  /^_fbp$/, // Meta Pixel
  /^_fbc$/,
  /^_hj/, // Hotjar
];

/** localStorage keys written by trackers or attribution. */
export const TRACKER_STORAGE_KEYS: readonly string[] = [
  '_gcl_ls',
  'blueticks_attribution_backup',
  'blueticks_utm_params',
  'blueticks_click_ids',
];

export function isTrackerCookieName(name: string): boolean {
  return TRACKER_COOKIE_PATTERNS.some(pattern => pattern.test(name));
}

/** Hosts that must receive no request before consent (spec section 11 checklist plus Google Ads). */
export const TRACKER_HOSTS: readonly string[] = [
  'googletagmanager.com',
  'google-analytics.com',
  'googleadservices.com',
  'googleads.g.doubleclick.net',
  'connect.facebook.net',
  'facebook.com',
  'static.hotjar.com',
  'script.hotjar.com',
  'cdn.firstpromoter.com',
  'm1.openfpcdn.io',
  'client.crisp.chat',
];

export function isTrackerHost(hostname: string): boolean {
  return TRACKER_HOSTS.some(host => hostname === host || hostname.endsWith('.' + host));
}

export type ConsentLogDomain = 'blueticks.co' | 'app.blueticks.co';

/**
 * The consent-log endpoint accepts only these two domains. Anything else (localhost, previews)
 * is not logged: consent evidence is for production traffic.
 */
export function consentLogDomain(hostname: string): ConsentLogDomain | null {
  if (hostname === 'blueticks.co' || hostname === 'www.blueticks.co') return 'blueticks.co';
  if (hostname === 'app.blueticks.co') return 'app.blueticks.co';
  // #1031: staging hosts log to the STAGING API (the caller passes the env API base and
  // `isLoggableOrigin` refuses a prod base from a non-prod host), labelled with the prod domain enum.
  if (hostname === 'app-staging.blueticks.co') return 'app.blueticks.co';
  return null;
}

/** Jurisdiction decision carried on the consent log. All fields optional on the wire (backend contract). */
export interface ConsentLogGeo {
  jurisdiction: Jurisdiction;
  /** ISO 3166-1 alpha-2, or 'XX' when the location is unknown. */
  country: string;
  /** ISO 3166-2 subdivision without the country prefix (e.g. 'QC'). Omitted when unknown. */
  region?: string;
  gpc?: boolean;
}

export interface ConsentLogPayload {
  consentId: string;
  choices: ConsentChoices;
  policyVersion: string;
  bannerVersion: string;
  source: ConsentSource;
  domain: ConsentLogDomain;
  jurisdiction?: Jurisdiction;
  country?: string;
  region?: string;
  gpc?: boolean;
}

export const LOG_COUNTRY_PATTERN = /^[A-Z]{2}$/;
export const LOG_REGION_PATTERN = /^[A-Z0-9]{1,3}$/;

export function buildConsentLogPayload(input: {
  consentId: string;
  choices: ConsentChoices;
  source: ConsentSource;
  domain: ConsentLogDomain;
  geo?: ConsentLogGeo;
}): ConsentLogPayload {
  const base: ConsentLogPayload = {
    consentId: input.consentId,
    choices: { analytics: input.choices.analytics, marketing: input.choices.marketing, functional: true },
    policyVersion: CONSENT_POLICY_VERSION,
    bannerVersion: CONSENT_BANNER_VERSION,
    source: input.source,
    domain: input.domain,
  };
  const geo = input.geo;
  if (!geo) return base;
  const country = LOG_COUNTRY_PATTERN.test(geo.country) ? geo.country : 'XX';
  const region = geo.region && LOG_REGION_PATTERN.test(geo.region) ? geo.region : undefined;
  return {
    ...base,
    jurisdiction: geo.jurisdiction,
    country,
    ...(region ? { region } : {}),
    ...(typeof geo.gpc === 'boolean' ? { gpc: geo.gpc } : {}),
  };
}

/** Prod hosts may write to any base; a non-prod host must never write to the prod API (#1031). */
export function isLoggableOrigin(hostname: string, apiBase: string): boolean {
  if (consentLogDomain(hostname) === null) return false;
  const prodHost = hostname === 'blueticks.co' || hostname === 'www.blueticks.co' || hostname === 'app.blueticks.co';
  return prodHost || apiBase.replace(/\/+$/, '') !== PROD_API_BASE;
}
